import type { ExpenseRow } from '../domain/expenseTypes';

const DUPLICATE_AMOUNT_EPS = 0.01;
const DUPLICATE_WINDOW_DAYS = 7;

export type DuplicateSupplierPaymentMatch =
  | {
      kind: 'purchase_linked';
      expense: ExpenseRow;
    }
  | {
      kind: 'similar_manual';
      expense: ExpenseRow;
    };

function isoDateAddDays(isoDate: string, deltaDays: number): string {
  const t = Date.parse(`${isoDate}T12:00:00`);
  if (Number.isNaN(t)) return isoDate;
  const d = new Date(t);
  d.setDate(d.getDate() + deltaDays);
  return d.toISOString().slice(0, 10);
}

function amountsMatch(a: number, b: number): boolean {
  return Math.abs(a - b) <= DUPLICATE_AMOUNT_EPS;
}

function withinWindow(expenseDate: string, candidateDate: string): boolean {
  const from = isoDateAddDays(expenseDate, -DUPLICATE_WINDOW_DAYS);
  const to = isoDateAddDays(expenseDate, DUPLICATE_WINDOW_DAYS);
  return candidateDate >= from && candidateDate <= to;
}

/**
 * ERP-style soft duplicate: mismo proveedor + monto ± ventana, o gasto ya ligado a OC.
 * No bloquea — la UI muestra banner.
 */
export function findDuplicateSupplierPayment(params: {
  branchId: string;
  supplierId: string | null;
  amount: number;
  expenseDate: string;
  rows: ExpenseRow[];
  excludeExpenseId?: string | null;
}): DuplicateSupplierPaymentMatch | null {
  const { branchId, supplierId, amount, expenseDate, rows, excludeExpenseId } = params;
  if (!supplierId || amount <= 0 || !expenseDate.trim()) return null;

  const candidates = rows
    .filter(
      (row) =>
        row.branchId === branchId &&
        row.status === 'active' &&
        row.id !== excludeExpenseId &&
        row.supplierId === supplierId &&
        withinWindow(expenseDate, row.expenseDate) &&
        amountsMatch(amount, row.amount),
    )
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));

  const linked = candidates.find((row) => row.purchaseOrderId != null);
  if (linked) return { kind: 'purchase_linked', expense: linked };

  const manual = candidates.find(
    (row) =>
      row.purchaseOrderId == null &&
      (row.categorySlug === 'supplier_payment' || row.categorySlug == null),
  );
  if (manual) return { kind: 'similar_manual', expense: manual };

  return null;
}
